import React from 'react';
import { safeToInterviewTypeLabel } from '../../../types/backendEnums';
import { Star, Copy, Check, Settings, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Interview } from '../../../types/recruiter';
import { Pagination } from '../../common/Pagination';
import { usePagination } from '../../../hooks/usePagination';

type InterviewStatusFilter =
  'all' | 'not_started' | 'in_progress' | 'completed' | 'evaluated' | 'cancelled';

interface InterviewSortConfig {
  key: keyof Interview | null;
  direction: 'asc' | 'desc';
}

interface InterviewsTabProps {
  interviews: Interview[];
  interviewSearchTerm: string;
  interviewStatusFilter: InterviewStatusFilter;
  setInterviewStatusFilter: (filter: InterviewStatusFilter) => void;
  interviewSortConfig: InterviewSortConfig;
  setInterviewSortConfig: (config: InterviewSortConfig) => void;
  formatCandidateName: (name: string) => string;
  generateInterviewUrl: (id: number) => string;
  getInterviewStatusColor: (status: string) => string;
  getInterviewStatusText: (status: string) => string;
  copyToClipboard: (url: string, id: number) => void;
  copiedUrls: Record<number, boolean>;
  onViewDetail: (interview: Interview) => void;
  onDeleteInterview: (interview: Interview) => void;
}

export const InterviewsTab: React.FC<InterviewsTabProps> = ({
  interviews,
  interviewSearchTerm,
  interviewStatusFilter,
  setInterviewStatusFilter,
  interviewSortConfig,
  setInterviewSortConfig,
  formatCandidateName,
  generateInterviewUrl,
  getInterviewStatusColor,
  getInterviewStatusText,
  copyToClipboard,
  copiedUrls,
  onViewDetail,
  onDeleteInterview,
}) => {
  // フィルタリングとソート
  const filteredAndSortedInterviews = React.useMemo(() => {
    let filtered = interviews;

    // 候補者名検索フィルタ
    if (interviewSearchTerm.trim()) {
      const searchLower = interviewSearchTerm.toLowerCase();
      filtered = filtered.filter((i) => i.candidateName.toLowerCase().includes(searchLower));
    }

    // ステータスフィルタ
    if (interviewStatusFilter !== 'all') {
      filtered = filtered.filter((i) => i.status === interviewStatusFilter);
    }

    // ソート
    if (interviewSortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[interviewSortConfig.key!];
        const bVal = b[interviewSortConfig.key!];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal < bVal) return interviewSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return interviewSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [interviews, interviewSearchTerm, interviewStatusFilter, interviewSortConfig]);

  // 面接一覧のページング。検索・ステータス絞り込み・ソートは全件に対して効かせ、
  // その結果をページごとに切り出す（API は全件返しているのでクライアント側で分割）。
  const tableRef = React.useRef<HTMLDivElement>(null);
  const pagination = usePagination(filteredAndSortedInterviews, {
    // 検索・ステータス絞り込み・ソートを変えたら1ページ目に戻す
    resetKey: `${interviewSearchTerm}|${interviewStatusFilter}|${interviewSortConfig.key ?? ''}|${interviewSortConfig.direction}`,
    // ページを変えたら一覧の先頭までスクロールする
    scrollTargetRef: tableRef,
  });

  // ソートハンドラ
  const handleSort = (key: keyof Interview) => {
    setInterviewSortConfig({
      key,
      direction:
        interviewSortConfig.key === key && interviewSortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  // ソートアイコン
  const getSortIcon = (key: keyof Interview) => {
    if (interviewSortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return interviewSortConfig.direction === 'asc' ? (
      <ArrowUp size={14} className="text-blue-600" />
    ) : (
      <ArrowDown size={14} className="text-blue-600" />
    );
  };

  // ステータスタブ
  const statusTabs: { key: InterviewStatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'すべて', count: interviews.length },
    {
      key: 'not_started',
      label: getInterviewStatusText('not_started'),
      count: interviews.filter((i) => i.status === 'not_started').length,
    },
    {
      key: 'in_progress',
      label: getInterviewStatusText('in_progress'),
      count: interviews.filter((i) => i.status === 'in_progress').length,
    },
    {
      key: 'completed',
      label: getInterviewStatusText('completed'),
      count: interviews.filter((i) => i.status === 'completed').length,
    },
    {
      key: 'evaluated',
      label: getInterviewStatusText('evaluated'),
      count: interviews.filter((i) => i.status === 'evaluated').length,
    },
    {
      key: 'cancelled',
      label: getInterviewStatusText('cancelled'),
      count: interviews.filter((i) => i.status === 'cancelled').length,
    },
  ];

  return (
    <div className="space-y-6">
      {/* ステータスタブ */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-2 overflow-x-auto" aria-label="Tabs">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setInterviewStatusFilter(tab.key);
                setInterviewSortConfig({ key: 'startDate', direction: 'desc' });
              }}
              className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 whitespace-nowrap ${
                interviewStatusFilter === tab.key
                  ? 'bg-white border-b-2 border-green-600'
                  : 'hover:bg-gray-50'
              }`}
            >
              {tab.key === 'all' ? (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                  {tab.label}
                </span>
              ) : (
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${getInterviewStatusColor(tab.key)}`}
                >
                  {tab.label}
                </span>
              )}
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  interviewStatusFilter === tab.key
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* テーブル表示 */}
      <div
        ref={tableRef}
        className="bg-white rounded-lg shadow-sm border border-gray-200 scroll-mt-4"
      >
        <div className="p-6 pb-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    候補者
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('startDate')}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('startDate')}
                    role="button"
                    tabIndex={0}
                    aria-label="面接可能期間でソート"
                  >
                    <div className="flex items-center gap-1">
                      面接可能期間
                      {getSortIcon('startDate')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('completedAt')}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('completedAt')}
                    role="button"
                    tabIndex={0}
                    aria-label="面接完了日時でソート"
                  >
                    <div className="flex items-center gap-1">
                      面接完了日時
                      {getSortIcon('completedAt')}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    面接種別
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    面接官
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    面接URL
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    スコア
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    アクション
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedInterviews.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                      該当する面接がありません
                    </td>
                  </tr>
                ) : (
                  pagination.pageItems.map((interview) => {
                    const currentUrl = interview.interviewUrl || generateInterviewUrl(interview.id);
                    return (
                      <tr key={interview.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCandidateName(interview.candidateName)}
                          </div>
                          <div className="text-sm text-gray-500">{interview.position}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {interview.startDate}
                          {interview.endDate && interview.endDate !== interview.startDate && (
                            <span className="text-gray-500"> 〜 {interview.endDate}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {interview.completedAt
                            ? new Date(interview.completedAt).toLocaleString('ja-JP', {
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {safeToInterviewTypeLabel(interview.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {interview.interviewer}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInterviewStatusColor(interview.status)}`}
                          >
                            {getInterviewStatusText(interview.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={currentUrl}
                              readOnly
                              className="w-40 px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs"
                            />
                            <button
                              onClick={() => copyToClipboard(currentUrl, interview.id)}
                              className={`px-2 py-1 rounded text-xs transition-colors ${
                                copiedUrls[interview.id]
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {copiedUrls[interview.id] ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {interview.score ? (
                            <div className="flex items-center">
                              <span className="mr-2">{interview.score}~点</span>
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    size={16}
                                    className={`${i < Math.floor((interview.score || 0) / 20) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">未評価</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => onViewDetail(interview)}
                              className="text-green-600 hover:text-green-800 transition-colors p-1"
                              title="詳細表示・URL編集"
                            >
                              <Settings size={16} />
                            </button>
                            <button
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="削除"
                              onClick={() => onDeleteInterview(interview)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          pageSize={pagination.pageSize}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          itemLabel="件"
        />
      </div>
    </div>
  );
};
